/**
 * Manage plan assignees (`PlanAssignees`, `reportsRoute === false`).
 * Maps XP plan assignments + location users → {@link TeamMemberRow}.
 */
import { type PlanAssignment } from '@cfa/pathway-fe-shared/xp';

import { getFilterableStatus } from '@/utils/getFilterableStatus';
import { getTimeSpentOnPlan } from '@/utils/time';

import Constants from '@/constants';

import type { TeamMemberRow } from '../teamMemberRow';

import {
  percentCompleteFromSteps,
  type PlanAssigneeNameSource,
  teamMemberNamesFromUser,
} from './planAssigneeRowShared';

type PlanAssigneeLocationUser = PlanAssigneeNameSource & {
  locationList?: string[];
};

const planAssignmentToTeamMemberRow = (
  assignment: PlanAssignment,
  {
    user,
    totalSteps,
  }: {
    user?: PlanAssigneeLocationUser;
    totalSteps: number;
  },
): TeamMemberRow => {
  const status = assignment.status ?? '';
  const completedStepCount = assignment.stepsComplete ?? 0;

  return {
    ...teamMemberNamesFromUser(user),
    id: assignment.userId ?? assignment.id ?? '',
    completionDate: assignment.completionDate,
    dueDate: assignment.dueDate,
    status,
    locations: user?.locationList,
    filterStatus: status ? getFilterableStatus(status, false) : 'not_completed',
    previouslyCompletedPlanAssignmentDate:
      assignment.latestCompletionReport?.completionDate ??
      assignment.completionDate ??
      '',
    timeSpentOnPlan: getTimeSpentOnPlan({
      steps: assignment.steps,
    }),
    percentComplete: percentCompleteFromSteps(completedStepCount, totalSteps),
    completedStepCount,
  };
};

export const planAssignmentsToTeamMemberRows = (
  assignments: PlanAssignment[],
  usersById: Map<string, PlanAssigneeLocationUser>,
  totalSteps: number,
): TeamMemberRow[] =>
  assignments
    .filter(
      assignment => assignment.status !== Constants.TRAINING_PLANS.UNASSIGNED,
    )
    .map(assignment => {
      const user = usersById.get(assignment.userId ?? '');
      return planAssignmentToTeamMemberRow(assignment, { user, totalSteps });
    });
